import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import api from '../lib/api';
import { mapScrapeToQuestionnaire, type ScrapeResult } from '../lib/mapScrapeToQuestionnaire';
import {
  mapExtractToQuestionnaire,
  type QuestionnaireExtractPayload,
  type SectionKey,
} from '../lib/mapExtractToQuestionnaire';
import { CompanySection } from '../components/questionnaire/CompanySection';
import { QuestionnaireQuickStart } from '../components/questionnaire/QuestionnaireQuickStart';
import { ProductSection } from '../components/questionnaire/ProductSection';
import { SalesSection } from '../components/questionnaire/SalesSection';
import { CompetitorsSection } from '../components/questionnaire/CompetitorsSection';
import { TeamSection } from '../components/questionnaire/TeamSection';
import { FileUploadZone, type UploadedFile } from '../components/questionnaire/FileUploadZone';

const SECTIONS: { key: SectionKey; label: string }[] = [
  { key: 'company', label: 'Company' },
  { key: 'product', label: 'Product' },
  { key: 'sales', label: 'Sales' },
  { key: 'competitors', label: 'Competitors' },
  { key: 'team', label: 'Team' },
];

const SECTION_KEYS = new Set<SectionKey>(SECTIONS.map((s) => s.key));

function parseQuestionnaireSectionParam(value: string | null): SectionKey | null {
  if (!value) return null;
  return SECTION_KEYS.has(value as SectionKey) ? (value as SectionKey) : null;
}

const defaultCompany = {
  website: '',
  industry: '',
  revenueModel: '',
  companySize: '',
  marketPosition: '',
};
const defaultProduct = {
  description: '',
  valueProposition: '',
  onboardingProcess: '',
  cost: '',
  pricingDetails: '',
  topFeedbackItems: [] as string[],
};
const defaultSales = {
  targetBuyers: '',
  topBuyerTitles: [] as string[],
  buyerChannels: '',
  salesProcessTimeline: [] as { stage: string; order: number }[],
  salesCycle: '',
  majorObjections: [] as string[],
  objectionHandling: '',
};
const defaultCompetitors = {
  competitors: [] as { name: string; website: string; differentiators: string; pricing: string }[],
};
const defaultTeam = { teamSize: 0, seniorityLevel: '', avgYearsExperience: 0 };

export function QuestionnairePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sectionFromUrl = searchParams.get('section');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [activeSection, setActiveSection] = useState<SectionKey>('company');
  const [questionnaireId, setQuestionnaireId] = useState('');

  const [company, setCompany] = useState(defaultCompany);
  const [product, setProduct] = useState(defaultProduct);
  const [sales, setSales] = useState(defaultSales);
  const [competitors, setCompetitors] = useState(defaultCompetitors);
  const [team, setTeam] = useState(defaultTeam);
  const [uploads, setUploads] = useState<Record<string, UploadedFile[]>>({
    company: [],
    product: [],
    sales: [],
    competitors: [],
    team: [],
    general: [],
  });

  // Track which sections have been touched for the stepper indicator
  const [savedSections, setSavedSections] = useState<Set<SectionKey>>(new Set());
  const [questionnaireCompleted, setQuestionnaireCompleted] = useState(false);

  const reviewMode = questionnaireCompleted;

  useEffect(() => {
    (async () => {
      try {
        // Ensure a questionnaire exists
        const createRes = await api.post('/questionnaire');
        setQuestionnaireId(createRes.data.id);

        const [orgRes, res] = await Promise.all([api.get('/orgs/me'), api.get('/questionnaire')]);
        setQuestionnaireCompleted(orgRes.data?.questionnaireCompleted === true);

        if (res.data) {
          const q = res.data;
          if (q.company) {
            setCompany({ ...defaultCompany, ...q.company });
            setSavedSections((s) => new Set(s).add('company'));
          }
          if (q.product) {
            setProduct({
              ...defaultProduct,
              ...q.product,
              topFeedbackItems: q.product.topFeedbackItems || [],
            });
            setSavedSections((s) => new Set(s).add('product'));
          }
          if (q.sales) {
            setSales({
              ...defaultSales,
              ...q.sales,
              topBuyerTitles: q.sales.topBuyerTitles || [],
              salesProcessTimeline: q.sales.salesProcessTimeline || [],
              majorObjections: q.sales.majorObjections || [],
            });
            setSavedSections((s) => new Set(s).add('sales'));
          }
          if (q.competitors) {
            setCompetitors({ competitors: q.competitors.competitors || [] });
            setSavedSections((s) => new Set(s).add('competitors'));
          }
          if (q.team) {
            setTeam({ ...defaultTeam, ...q.team });
            setSavedSections((s) => new Set(s).add('team'));
          }
          if (q.currentSection) setActiveSection(q.currentSection as SectionKey);

          // Group uploads by section
          if (q.uploads) {
            const grouped: Record<string, UploadedFile[]> = {
              company: [],
              product: [],
              sales: [],
              competitors: [],
              team: [],
              general: [],
            };
            q.uploads.forEach((u: UploadedFile & { section: string }) => {
              if (grouped[u.section]) grouped[u.section].push(u);
              else grouped.general.push(u);
            });
            setUploads(grouped);
          }
        }
      } catch (err) {
        console.error('Failed to load questionnaire:', err);
        Sentry.captureException(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (loading) return;
    const s = parseQuestionnaireSectionParam(sectionFromUrl);
    if (s) setActiveSection(s);
  }, [loading, sectionFromUrl]);

  const saveSection = useCallback(
    async (section: SectionKey) => {
      setSaving(true);
      try {
        const data = { company, product, sales, competitors, team }[section];
        await api.patch(`/questionnaire/${section}`, data);
        setSavedSections((s) => new Set(s).add(section));
      } catch (err) {
        console.error(`Failed to save ${section}:`, err);
        Sentry.captureException(err);
      } finally {
        setSaving(false);
      }
    },
    [company, product, sales, competitors, team],
  );

  const handleSaveAndContinue = async () => {
    await saveSection(activeSection);
    const currentIndex = SECTIONS.findIndex((s) => s.key === activeSection);
    if (currentIndex < SECTIONS.length - 1) {
      setActiveSection(SECTIONS[currentIndex + 1].key);
    }
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await saveSection(activeSection);
      await api.post('/questionnaire/complete');
      navigate(reviewMode ? '/personas' : '/');
    } catch (err) {
      console.error('Failed to complete questionnaire:', err);
      Sentry.captureException(err);
    } finally {
      setCompleting(false);
    }
  };

  const handleUploadsChange = (section: string) => (newUploads: UploadedFile[]) => {
    setUploads((prev) => ({ ...prev, [section]: newUploads }));
  };

  const handleScrapeComplete = useCallback(
    async (scrape: ScrapeResult): Promise<string[]> => {
      const resolvedWebsite = scrape.resolvedUrl?.trim();
      const companyForMap = resolvedWebsite ? { ...company, website: resolvedWebsite } : company;
      const mapped = mapScrapeToQuestionnaire(scrape, {
        company: companyForMap,
        product,
        sales,
      });

      setCompany(mapped.company);
      setProduct(mapped.product);
      setSales(mapped.sales);

      const patchPromises = mapped.updatedSections.map((section) => {
        const body = { company: mapped.company, product: mapped.product, sales: mapped.sales }[
          section
        ];
        return api.patch(`/questionnaire/${section}`, body).then(() => section);
      });

      const settled = await Promise.allSettled(patchPromises);
      const saved: string[] = [];
      for (const result of settled) {
        if (result.status === 'fulfilled') {
          const section = result.value as SectionKey;
          saved.push(section);
          setSavedSections((s) => new Set(s).add(section));
        }
      }
      return saved;
    },
    [company, product, sales],
  );

  const handleExtractComplete = useCallback(
    async (payload: QuestionnaireExtractPayload): Promise<string[]> => {
      const mapped = mapExtractToQuestionnaire(payload, {
        company,
        product,
        sales,
        competitors,
        team,
      });

      setCompany(mapped.company);
      setProduct(mapped.product);
      setSales(mapped.sales);
      setCompetitors(mapped.competitors);
      setTeam(mapped.team);

      const patchPromises = mapped.updatedSections.map((section) => {
        const dataMap = {
          company: mapped.company,
          product: mapped.product,
          sales: mapped.sales,
          competitors: mapped.competitors,
          team: mapped.team,
        } as const;
        const body = dataMap[section];
        return api.patch(`/questionnaire/${section}`, body).then(() => section);
      });

      const settled = await Promise.allSettled(patchPromises);
      const saved: string[] = [];
      for (const result of settled) {
        if (result.status === 'fulfilled') {
          const section = result.value as SectionKey;
          saved.push(section);
          setSavedSections((s) => new Set(s).add(section));
        }
      }
      return saved;
    },
    [company, product, sales, competitors, team],
  );

  if (loading) return <div className="text-gray-500">Loading business profile...</div>;

  const currentIndex = SECTIONS.findIndex((s) => s.key === activeSection);
  const isLastSection = currentIndex === SECTIONS.length - 1;

  return (
    <div>
      <div className="mb-8">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {reviewMode ? 'Business profile' : 'Set up your business profile'}
            </h1>
            <p className="text-gray-600 mt-1">
              {reviewMode
                ? 'Review or update your answers. Saving on the last step syncs your organization profile; you can then regenerate AI personas on the Personas page.'
                : 'Tell us about your business so we can build your ideal practice environment.'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="shrink-0 ml-4 text-sm text-gray-500 hover:text-gray-700 underline"
          >
            {reviewMode ? 'Back to dashboard' : 'Skip for now'}
          </button>
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
            <span>
              {savedSections.size} of {SECTIONS.length} sections complete
            </span>
            <span>{Math.round((savedSections.size / SECTIONS.length) * 100)}%</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-600 rounded-full transition-all duration-300"
              style={{ width: `${(savedSections.size / SECTIONS.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <section
        className="mb-8 rounded-xl border border-green-200 bg-gradient-to-br from-green-50/90 to-white p-5 sm:p-6 shadow-sm"
        aria-labelledby="business-profile-quick-start-heading"
      >
        <h2
          id="business-profile-quick-start-heading"
          className="text-lg font-semibold text-gray-900"
        >
          Quick start
        </h2>
        <p className="text-sm text-gray-600 mt-2 max-w-4xl leading-relaxed">
          Let us draft your profile from the sources below—you review and edit, or skip and add
          everything yourself.
        </p>
        <div className="mt-5">
          <QuestionnaireQuickStart
            data={company}
            onChange={setCompany}
            questionnaireId={questionnaireId}
            uploads={uploads.company}
            generalUploads={uploads.general}
            onUploadsChange={handleUploadsChange('company')}
            onScrapeComplete={handleScrapeComplete}
            onExtractComplete={handleExtractComplete}
          />
        </div>
      </section>

      {/* Mobile horizontal stepper */}
      <div className="md:hidden flex gap-2 overflow-x-auto pb-3 mb-4 -mx-1 px-1">
        {SECTIONS.map((section, index) => (
          <button
            key={section.key}
            onClick={() => setActiveSection(section.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap shrink-0 transition-colors ${
              activeSection === section.key
                ? 'bg-green-100 text-green-800 ring-1 ring-green-600'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            <span
              className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                savedSections.has(section.key)
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-300 text-gray-600'
              }`}
            >
              {savedSections.has(section.key) ? '✓' : index + 1}
            </span>
            {section.label}
          </button>
        ))}
      </div>

      <div className="flex gap-8">
        {/* Sidebar stepper (desktop only) */}
        <nav className="hidden md:block w-48 shrink-0">
          <ul className="space-y-1">
            {SECTIONS.map((section, index) => (
              <li key={section.key}>
                <button
                  onClick={() => setActiveSection(section.key)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors
                    ${
                      activeSection === section.key
                        ? 'bg-green-50 text-green-800 border-l-2 border-green-700'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                >
                  <span className="flex items-center gap-2">
                    <span
                      className={`w-5 h-5 rounded-full flex items-center justify-center text-xs
                      ${
                        savedSections.has(section.key)
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {savedSections.has(section.key) ? '✓' : index + 1}
                    </span>
                    {section.label}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          <div className="bg-white p-6 rounded-lg border">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {SECTIONS[currentIndex].label}
            </h2>

            {activeSection === 'company' && <CompanySection data={company} onChange={setCompany} />}
            {activeSection === 'product' && (
              <ProductSection
                data={product}
                onChange={setProduct}
                questionnaireId={questionnaireId}
                uploads={uploads.product}
                onUploadsChange={handleUploadsChange('product')}
              />
            )}
            {activeSection === 'sales' && (
              <SalesSection
                data={sales}
                onChange={setSales}
                questionnaireId={questionnaireId}
                uploads={uploads.sales}
                onUploadsChange={handleUploadsChange('sales')}
              />
            )}
            {activeSection === 'competitors' && (
              <CompetitorsSection
                data={competitors}
                onChange={setCompetitors}
                questionnaireId={questionnaireId}
                uploads={uploads.competitors}
                onUploadsChange={handleUploadsChange('competitors')}
              />
            )}
            {activeSection === 'team' && (
              <TeamSection
                data={team}
                onChange={setTeam}
                questionnaireId={questionnaireId}
                uploads={uploads.team}
                onUploadsChange={handleUploadsChange('team')}
              />
            )}

            <div className="mt-6 pt-4 border-t flex flex-wrap items-center justify-between gap-3">
              <div className="flex gap-3">
                {currentIndex > 0 && (
                  <button
                    type="button"
                    onClick={() => setActiveSection(SECTIONS[currentIndex - 1].key)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Back
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSaveAndContinue}
                  disabled={saving}
                  className="px-4 py-2 bg-green-700 text-white rounded-md text-sm font-medium hover:bg-green-800 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : isLastSection ? 'Save' : 'Save & Continue'}
                </button>
              </div>

              {isLastSection && (
                <button
                  type="button"
                  onClick={handleComplete}
                  disabled={completing}
                  className="px-6 py-2 bg-green-800 text-white rounded-md text-sm font-medium hover:bg-green-900 disabled:opacity-50"
                >
                  {completing
                    ? reviewMode
                      ? 'Saving...'
                      : 'Completing...'
                    : reviewMode
                      ? 'Save & update profile'
                      : 'Complete business profile'}
                </button>
              )}
            </div>
          </div>

          {/* General document upload area */}
          <div className="mt-6 bg-white p-6 rounded-lg border">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Additional Documents</h3>
            <p className="text-xs text-gray-500 mb-3">
              Upload any other relevant materials: pitch decks, sales decks, white papers, etc.
            </p>
            <FileUploadZone
              questionnaireId={questionnaireId}
              section="general"
              uploads={uploads.general}
              onUploadsChange={handleUploadsChange('general')}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
